<template>
  <section class="hero">
    <!-- 左侧内容 -->
    <div class="left-content">

      <!-- 新增引导标签 -->
      <div class="guidance-tag">
        <span class="square-dot"></span> Professional Guidance for Crypto Fraud & Digital Asset Loss
      </div>

      <h1 class="title">Lost Cryptocurrency <br> to a Scam ? <br> <span class="highlight-cyan">Phishing Attack</span> or
        <br> <span class="highlight-green">Fraudulent Platform?</span>
      </h1>
      <p class="desc">When cryptocurrency is lost to fraud, phishing, investment scams, wallet compromise, or
        unauthorized transactions, knowing what to do next can be difficult.</p>
      <p class="desc">Fraud Counsel Department provides structured case assessment, blockchain transaction analysis, and
        recovery assistance.</p>

      <div class="buttons">
        <button class="btn-primary">Start Recovery →</button>
        <button class="btn-secondary">Recovery Services</button>
      </div>

      <TerminalWindow />
    </div>

    <!-- 右侧 3D 区域（窄屏时强制排在最上面） -->
    <div class="right-visual" ref="threeContainer"></div>
  </section>
</template>

<script>
import TerminalWindow from './TerminalWindow.vue';
import * as THREE from 'three';

export default {
  components: { TerminalWindow },
  data() {
    return {
      renderer: null,
      scene: null,
      camera: null,
      animationId: null,
      shieldGroup: null,
      scanPlane: null,
      orbitBall: null,
      orbitRadius: 2.8,
      resizeHandler: null
    };
  },
  mounted() {
    this.initThree();
    this.animate();
    this.resizeHandler = () => this.onResize();
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resizeHandler);
    this.scene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        Array.isArray(obj.material) ? obj.material.forEach(m => m.dispose()) : obj.material.dispose();
      }
    });
    this.renderer.dispose();
  },
  methods: {
    // 创建文字纹理
    createTextTexture() {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#34ffb0';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.font = 'bold 100px Rajdhani, sans-serif';
      ctx.fillText('RECOVERY READY', 512, 256);

      ctx.font = '32px Rajdhani, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('YOUR ASSETS ARE SAFE WITH US', 512, 380);

      const texture = new THREE.CanvasTexture(canvas);
      return texture;
    },

    // 创建渐变扫描线材质
    createScanTexture() {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 1024;
      const ctx = canvas.getContext('2d');
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);

      gradient.addColorStop(0, 'rgba(52, 255, 176, 0)');
      gradient.addColorStop(0.5, 'rgba(52, 255, 176, 1)');
      gradient.addColorStop(1, 'rgba(52, 255, 176, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      return new THREE.CanvasTexture(canvas);
    },

    initThree() {
      const container = this.$refs.threeContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      this.camera.position.set(0, 0, 8);

      this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      this.renderer.setSize(width, height);
      this.renderer.setClearColor(0x000000, 0);
      container.appendChild(this.renderer.domElement);

      // 灯光
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
      const dirLight = new THREE.DirectionalLight(0x34ffb0, 1);
      dirLight.position.set(5, 5, 5);
      this.scene.add(dirLight);

      // --- 1. 构造多层盾牌组 ---
      this.shieldGroup = new THREE.Group();

      const shieldShape = new THREE.Shape();
      shieldShape.moveTo(0, 2);
      shieldShape.lineTo(-1.5, 1.2);
      shieldShape.lineTo(-1.5, -0.5);
      shieldShape.lineTo(0, -2);
      shieldShape.lineTo(1.5, -0.5);
      shieldShape.lineTo(1.5, 1.2);
      shieldShape.closePath();

      const extrudeSettings = { depth: 0.4, bevelEnabled: true, bevelSize: 0.05, bevelThickness: 0.05 };

      // 底层：黑色实心盾牌 (作为背景底座)
      const blackGeo = new THREE.ExtrudeGeometry(shieldShape, extrudeSettings);
      const blackMat = new THREE.MeshPhongMaterial({ color: 0x000000, emissive: 0x001100, side: THREE.DoubleSide });
      const blackShield = new THREE.Mesh(blackGeo, blackMat);
      blackShield.position.z = -0.3;
      this.shieldGroup.add(blackShield);

      // 中层：半透明绿色实心盾牌
      const greenGeo = new THREE.ExtrudeGeometry(shieldShape, { ...extrudeSettings, depth: 0.2 });
      const greenMat = new THREE.MeshPhongMaterial({
        color: 0x34ffb0,
        transparent: true,
        opacity: 0.4,
        emissive: 0x112211,
        side: THREE.DoubleSide
      });
      const greenShield = new THREE.Mesh(greenGeo, greenMat);
      greenShield.position.z = 0;
      this.shieldGroup.add(greenShield);

      // 外层：三角网格线框盾牌 (展现多层面的镂空感)
      const outerGeo = new THREE.ExtrudeGeometry(shieldShape, extrudeSettings);
      const outerMat = new THREE.MeshBasicMaterial({
        color: 0x34ffb0,
        wireframe: true,
        transparent: true,
        opacity: 0.8
      });
      const outerShield = new THREE.Mesh(outerGeo, outerMat);
      outerShield.position.z = 0.2;
      outerShield.scale.set(1.1, 1.1, 1); // 放大一点，形成嵌套感
      this.shieldGroup.add(outerShield);

      // --- 2. 深色锁图形 ---
      const lockGroup = new THREE.Group();
      const lockMat = new THREE.MeshPhongMaterial({ color: 0x00442a, emissive: 0x001100 });
      const lockBody = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.8, 0.3), lockMat);
      const lockShackle = new THREE.Mesh(new THREE.TorusGeometry(0.4, 0.1, 8, 16, Math.PI), lockMat);
      lockShackle.position.y = 0.5;
      lockGroup.add(lockBody);
      lockGroup.add(lockShackle);
      lockGroup.position.set(0, 0.3, 0.4);
      this.shieldGroup.add(lockGroup);

      // --- 3. 锁下方的超大文字 Mesh ---
      const textGeo = new THREE.PlaneGeometry(4.5, 2.25);
      const textMat = new THREE.MeshBasicMaterial({
        map: this.createTextTexture(),
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const textMesh = new THREE.Mesh(textGeo, textMat);
      textMesh.position.set(0, -1.4, 0.5);
      this.shieldGroup.add(textMesh);

      this.scene.add(this.shieldGroup);

      // --- 4. 竖直平面的轨道圈 (静止) ---
      const orbitGroup = new THREE.Group();
      const orbitPoints = [];
      for (let i = 0; i <= 64; i++) {
        const angle = (i / 64) * Math.PI * 2;
        orbitPoints.push(new THREE.Vector3(this.orbitRadius * Math.cos(angle), this.orbitRadius * Math.sin(angle), 0));
      }
      const orbitGeo = new THREE.BufferGeometry().setFromPoints(orbitPoints);
      const orbitLine = new THREE.Line(orbitGeo, new THREE.LineBasicMaterial({ color: 0x34ffb0, opacity: 0.4, transparent: true }));
      orbitGroup.add(orbitLine);

      // 匀速运动的点
      const ballGeo = new THREE.SphereGeometry(0.15, 16, 16);
      const ballMat = new THREE.MeshBasicMaterial({ color: 0xffffff, emissive: 0x34ffb0 });
      this.orbitBall = new THREE.Mesh(ballGeo, ballMat);
      orbitGroup.add(this.orbitBall);

      this.scene.add(orbitGroup);

      // --- 5. 上下扫描光线 ---
      const scanGeo = new THREE.PlaneGeometry(3.2, 2);
      const scanMat = new THREE.MeshBasicMaterial({
        map: this.createScanTexture(),
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      this.scanPlane = new THREE.Mesh(scanGeo, scanMat);
      this.scanPlane.position.z = 0.6;
      this.shieldGroup.add(this.scanPlane);
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      const time = Date.now() * 0.001;

      this.shieldGroup.rotation.y = Math.PI * Math.sin(time * 0.5);

      this.orbitBall.position.x = this.orbitRadius * Math.cos(time * 1.2);
      this.orbitBall.position.y = this.orbitRadius * Math.sin(time * 1.2);

      const duration = 3;
      const progress = (time % duration) / duration;
      this.scanPlane.position.y = 2 - (progress * 5);

      this.renderer.render(this.scene, this.camera);
    },

    onResize() {
      const container = this.$refs.threeContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }
  }
}
</script>

<style scoped>
.hero {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  padding: 150px 10% 50px;
  min-height: 100vh;
  align-items: center;
  gap: 40px;
}

/* 新增引导标签样式 */
.guidance-tag {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(52, 255, 176, 0.3);
  /* 很细的边框，颜色比较淡 */
  padding: 10px 18px;
  border-radius: 4px;
  color: #34ffb0;
  /* 与字体颜色一致 */
  font-size: 0.9rem;
  margin-bottom: 25px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* 闪烁的小方点 */
.square-dot {
  width: 8px;
  height: 8px;
  background-color: #34ffb0;
  display: inline-block;
  animation: blink-dot 1.2s infinite;
}

@keyframes blink-dot {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.2;
  }
}

/* 左侧排版 */
.title {
  font-family: 'Rajdhani', sans-serif;
  font-weight: 800;
  font-size: 3.5rem;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.1;
}

.highlight-cyan {
  background: linear-gradient(90deg, #00e5ff, #6ff);
  -webkit-background-clip: text;
  color: transparent;
}

.highlight-green {
  background: linear-gradient(90deg, #34ffb0, #0f8);
  -webkit-background-clip: text;
  color: transparent;
}

.desc {
  color: #a8b3cf;
  font-size: 1.1rem;
  margin-bottom: 30px;
  line-height: 1.6;
  max-width: 600px;
}

/* 按钮组 */
.buttons {
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
}

.btn-primary {
  background: linear-gradient(90deg, #00e5ff, #34ffb0);
  color: #000;
  font-weight: bold;
  border: none;
  padding: 15px 30px;
  box-shadow: 0 0 20px rgba(52, 255, 176, 0.6);
  cursor: pointer;
  font-size: 1rem;
}

.btn-secondary {
  background: transparent;
  border: 1px solid #333;
  color: #fff;
  padding: 15px 30px;
  cursor: pointer;
  font-size: 1rem;
}

/* 3D区域 */
.right-visual {
  position: relative;
  width: 100%;
  height: 500px;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

/* 响应式：窄屏强制把 3D 放最上面，且不隐藏 */
@media (max-width: 900px) {
  .hero {
    grid-template-columns: 1fr;
    padding-top: 120px;
  }

  .right-visual {
    order: -1;
    height: 350px;
  }

  .title {
    font-size: 2.5rem;
  }
}
</style>